/**
 * Presentation-only layout helpers for Invoice Preview/PDF.
 * Splits existing header.fields into Bill To vs Invoice Details — no new data.
 */

export const INVOICE_BILL_TO_FIELD_KEYS = Object.freeze([
  "client",
  "clientContact",
  "clientAddress",
  "clientEmail",
  "clientPhone",
]);

export const INVOICE_DETAILS_FIELD_KEYS = Object.freeze([
  "invoiceNumber",
  "invoiceDate",
  "status",
  "project",
  "projectNumber",
  "projectContact",
  "siteAddress",
  "sourceEstimate",
  "clientReference",
  "billingPeriod",
]);

const CORPORATE_SUFFIX_RE =
  /^(inc|incorporated|ltd|limited|llc|l\.l\.c|corp|corporation|co|company)\.?$/i;

function normalizeNameToken(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[.,]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Decide how to show legal name under the trading name.
 * Suffix-only differences (Inc., Ltd., etc.) stay visible but subdued.
 *
 * @returns {{ text: string, emphasis: "subtle" | "normal" } | null}
 */
export function resolveInvoiceLegalNamePresentation(brandName, legalNameLine) {
  const legal = String(legalNameLine || "").trim();
  if (!legal) return null;

  const brand = String(brandName || "").trim();
  if (!brand || normalizeNameToken(brand) === normalizeNameToken(legal)) {
    return null;
  }

  const brandNorm = normalizeNameToken(brand);
  const legalNorm = normalizeNameToken(legal);

  if (legalNorm.startsWith(brandNorm)) {
    const remainder = legalNorm.slice(brandNorm.length).trim();
    if (!remainder || CORPORATE_SUFFIX_RE.test(remainder)) {
      return { text: legal, emphasis: "subtle" };
    }
  }

  return { text: legal, emphasis: "normal" };
}

function fieldMap(fields = []) {
  const map = new Map();
  for (const field of fields || []) {
    if (field?.key) map.set(field.key, field);
  }
  return map;
}

/**
 * @returns {{
 *   billTo: { title: string, name: string|null, lines: string[] },
 *   details: { title: string, rows: Array<{ label: string, value: string }> },
 * }}
 */
export function buildInvoicePartiesLayout(fields = []) {
  const byKey = fieldMap(fields);

  const clientField = byKey.get("client");
  const billToLines = [];
  for (const key of INVOICE_BILL_TO_FIELD_KEYS) {
    if (key === "client") continue;
    const field = byKey.get(key);
    if (field?.value) billToLines.push(String(field.value));
  }

  const detailRows = [];
  for (const key of INVOICE_DETAILS_FIELD_KEYS) {
    const field = byKey.get(key);
    if (!field?.value) continue;
    detailRows.push({
      label: field.label,
      value: String(field.value),
    });
  }

  return {
    billTo: {
      title: "Bill To",
      name: clientField?.value ? String(clientField.value) : null,
      lines: billToLines,
    },
    details: {
      title: "Invoice Details",
      rows: detailRows,
    },
  };
}

/**
 * Visible section subtotals for the invoice totals block (presentation only).
 */
export function getVisibleInvoiceSectionSubtotals(reportModel) {
  const ordered = Array.isArray(reportModel?.sectionsOrdered)
    ? reportModel.sectionsOrdered
    : [];

  return ordered
    .filter(
      (entry) =>
        entry?.section &&
        entry.section.mode !== "hidden" &&
        Number(entry.section.subtotal) > 0,
    )
    .map((entry) => ({
      id: entry.id,
      label: `${entry.title} Subtotal`,
      amount: Number(entry.section.subtotal || 0),
    }));
}
